import type { AriaAttributes, AriaRole, FC, ReactNode } from 'react';
import type {
  ComponentVariant,
  ComponentAppearance,
  BaseProps,
} from 'src/modules/shared/types/component.type';
import { getDisabledClasses } from 'src/modules/shared/styles/component.styles';
import { Typography } from '@material-tailwind/react';
import {
  baseAlertClasses,
  alertSizeClasses,
  alertTextSizeClasses,
  alertFilledClasses,
  alertSoftClasses,
  alertOutlineClasses,
  alertGhostClasses,
} from './alert.styles';

/** Props for the shared alert presentation primitive. */
export interface AlertProps extends BaseProps {
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Alert content. */
  message: string | ReactNode;
  /** Optional trailing action, aligned right and stacked on small screens. */
  action?: ReactNode;
  /** Visual appearance of the alert. */
  appearance?: ComponentAppearance;
  /** Semantic role announced by assistive technology. */
  role?: AriaRole;
  /** Live-region priority used when alert content changes. */
  ariaLive?: AriaAttributes['aria-live'];
}

const appearanceClasses: Record<
  ComponentAppearance,
  Record<NonNullable<ComponentVariant>, string>
> = {
  filled: alertFilledClasses,
  outline: alertOutlineClasses,
  soft: alertSoftClasses,
  ghost: alertGhostClasses,
};

export const Alert: FC<AlertProps> = ({
  icon,
  message,
  action,
  className = '',
  variant = 'primary',
  appearance = 'soft',
  size = 'sm',
  disabled = false,
  role,
  ariaLive,
}) => {
  const classes = [
    ...baseAlertClasses,
    appearanceClasses[appearance][variant],
    alertSizeClasses[size],
    getDisabledClasses(disabled),
    action ? 'flex-col sm:flex-row' : '',
    className,
  ].join(' ');

  const content = (
    <>
      {icon && <div className="flex-shrink-0 self-start">{icon}</div>}
      {typeof message === 'string' ? (
        <Typography className={alertTextSizeClasses[size]}>
          {message}
        </Typography>
      ) : (
        message
      )}
    </>
  );

  return (
    <div className={classes} role={role} aria-live={ariaLive}>
      {action ? (
        <div className="flex w-full min-w-0 flex-1 items-center gap-2">
          {content}
        </div>
      ) : (
        content
      )}
      {action && (
        <div className="shrink-0 self-end sm:self-center">{action}</div>
      )}
    </div>
  );
};
